import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import { emailApi } from '../utils/emailApi';

const FooterWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: '#0A1628',
  color: '#FFFFFF',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.3), transparent)',
  },
}));

const FooterTop = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(6),
}));

const FooterBottom = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
}));

const FooterTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1rem',
  marginBottom: theme.spacing(3),
  color: '#FFFFFF',
}));

const FooterLink = styled(Link)(({ theme }) => ({
  display: 'block',
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '0.9375rem',
  marginBottom: theme.spacing(1.5),
  textDecoration: 'none',
  transition: 'color 0.2s ease',

  '&:hover': {
    color: theme.palette.primary.main,
  },
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  marginRight: theme.spacing(1),
  transition: 'all 0.3s ease',

  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: '#FFFFFF',
    transform: 'translateY(-3px)',
  },
}));

const NewsletterField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: '8px',
    color: '#FFFFFF',

    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },

    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },

    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },

  '& .MuiInputBase-input': {
    color: '#FFFFFF',
    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.5)',
      opacity: 1,
    },
  },
}));

const quickLinks = [
  { path: '/', label: 'nav.home' },
  { path: '/about', label: 'nav.about' },
  { path: '/services', label: 'footer.services' },
  { path: '/products', label: 'footer.products' },
  { path: '/contact', label: 'footer.contact' },
];

const legalLinks = [
  { path: '/privacy', label: 'footer.privacy' },
  { path: '/terms', label: 'footer.terms' },
  { path: '/cookies', label: 'footer.cookies' },
];

const socialLinks = [
  { icon: <FacebookIcon fontSize="small" />, label: 'Facebook', url: 'https://www.facebook.com/nexfahtechnologysolutions' },
  { icon: <InstagramIcon fontSize="small" />, label: 'Instagram', url: 'https://www.instagram.com/nexfahtechnologysolutions' },
  { icon: <LinkedInIcon fontSize="small" />, label: 'LinkedIn', url: 'https://linkedin.com/company/nexfahtechnology' },
];

export default function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await emailApi.sendNewsletter({ email });

      // Track newsletter event (GA4)
      if (window.gtag) {
        window.gtag('event', 'newsletter_subscribe', {
          event_category: 'engagement',
        });
      }

      setSuccess(true);
      setEmail('');
      setSnackbar({
        open: true,
        message: 'Successfully subscribed to newsletter!',
        severity: 'success',
      });

      // Reset success state after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Newsletter error:', error);
      setSnackbar({
        open: true,
        message: 'Failed to subscribe. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <FooterWrapper>
      <Container maxWidth="lg">
        <FooterTop>
          <Grid container spacing={4}>
            {/* Company Info */}
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 3 }}>
                <img
                  src="/logo.png"
                  alt="Nexfah"
                  style={{
                    height: '40px',
                    filter: 'brightness(0) invert(1)',
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  lineHeight: 1.8,
                  mb: 3,
                  maxWidth: '300px',
                }}
              >
                {t('footer.description')}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 1.5 }}
              >
                {t('footer.followUs')}
              </Typography>
              <Box>
                {socialLinks.map((social) => (
                  <SocialButton
                    key={social.label}
                    aria-label={social.label}
                    size="small"
                    component="a"
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {social.icon}
                  </SocialButton>
                ))}
              </Box>
            </Grid>

            {/* Quick Links */}
            <Grid item xs={6} sm={4} md={2}>
              <FooterTitle>{t('footer.quickLinks')}</FooterTitle>
              {quickLinks.map((link) => (
                <FooterLink key={link.path} to={link.path}>
                  {t(link.label)}
                </FooterLink>
              ))}
            </Grid>

            {/* Legal */}
            <Grid item xs={6} sm={4} md={2}>
              <FooterTitle>{t('footer.legal')}</FooterTitle>
              {legalLinks.map((link) => (
                <FooterLink key={link.path} to={link.path}>
                  {t(link.label)}
                </FooterLink>
              ))}
            </Grid>

            {/* Newsletter */}
            <Grid item xs={12} sm={8} md={4}>
              <FooterTitle>{t('footer.newsletter.title')}</FooterTitle>
              <Box
                component="form"
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
                onSubmit={handleNewsletterSubmit}
              >
                <NewsletterField
                  placeholder={t('footer.newsletter.placeholder')}
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading || !email}
                  sx={{
                    background: success
                      ? '#10b981'
                      : (theme) => theme.palette.accent.gradient,
                    minWidth: { xs: '100%', sm: 'auto' },
                    px: 3,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      background: success
                        ? '#10b981'
                        : (theme) => theme.palette.accent.gradientHover,
                    },
                    '&.Mui-disabled': {
                      background: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  endIcon={
                    loading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : success ? (
                      <CheckIcon />
                    ) : (
                      <ArrowForwardIcon />
                    )
                  }
                >
                  {success ? 'Subscribed!' : t('footer.newsletter.button')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </FooterTop>

        <FooterBottom>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
            >
              {t('footer.copyright').replace('2024', currentYear.toString())}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
            >
              Designed & Built with passion
            </Typography>
          </Box>
        </FooterBottom>
      </Container>

      {/* Snackbar for newsletter feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: '12px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </FooterWrapper>
  );
}
